import { useRef, useState } from 'react';
import styled from 'styled-components';

import {
	Button,
	Divider,
	Form,
	FormInstance,
	Input,
	InputNumber,
	Select,
	Slider,
	Switch,
	notification,
} from 'antd';

import * as CmsPageService from '../../../../services/bb-api/cms/page';
import type { Page } from '../../../../services/bb-api/cms/page';

import { txt } from '../../../../locales/i18n';
import { formatDate } from '../../../../utils/date';

export type PageFormProps = {
	visible: boolean;
	page: Page;
	setPage: (page: Page) => void;
};

export const PageForm = (props: PageFormProps) => {
	const [isUpdating, setIsUpdating] = useState(false);
	const formRef = useRef<FormInstance>(null);

	const handleSavePage = async (pageValues: any) => {
		const form = formRef.current;
		if (form === null) return;

		const [urlError = null] = form.getFieldError('url');
		if (urlError !== null) return notification.error({ message: urlError });

		setIsUpdating(true);

		try {
			const page = await CmsPageService.update(props.page.id, pageValues);
			props.setPage(page);

			notification.success({ message: txt('notification.update successful') });
		} catch (error) {
			if (error instanceof Error && error.message === 'url already exists') {
				const errorMessage = txt('cms.notifications.urlExists');
				form.setFields([{ name: 'url', errors: [errorMessage] }]);
			}

			notification.error({ message: txt('notification.update failed') });
		}

		setIsUpdating(false);
	};

	const handleValuesChange = (values: any) => {
		const form = formRef.current;
		if (form === null) return;

		const [[key, value]] = Object.entries(values);

		if (key === 'title') {
			form.setFields([{ name: key, errors: [] }]);

			const maxTitleLength = 60;
			const titleLength = (value as string).length;
			if (titleLength < maxTitleLength) return;

			const errorMessage = txt('cms.notifications.titleLength', {
				maxTitleLength,
			});

			form.setFields([{ name: key, errors: [errorMessage] }]);
		}

		if (key === 'metaDescription') {
			form.setFields([{ name: key, errors: [] }]);

			const maxMetaDescriptionLength = 160;
			const metaDescriptionLength = (value as string).length;
			if (metaDescriptionLength < maxMetaDescriptionLength) return;

			const errorMessage = txt('cms.notifications.metaDescriptionLength', {
				maxMetaDescriptionLength,
			});

			form.setFields([{ name: key, errors: [errorMessage] }]);
		}

		if (key === 'url') {
			form.setFields([{ name: key, errors: [] }]);
			const urlPattern = /^\w[/\w-]*$/gi;
			if (urlPattern.test(value as string)) return;

			const errorMessage = txt('cms.notifications.urlFormat');
			form.setFields([{ name: key, errors: [errorMessage] }]);
		}
	};

	if (props.visible === false) return null;

	return (
		<FormStyled
			initialValues={props.page}
			onFinish={handleSavePage}
			onValuesChange={handleValuesChange}
			ref={formRef}
		>
			<div className="inputs-container">
				<h3>{txt('cms.general')}</h3>

				<label>{txt('cms.title')}</label>
				<Form.Item name="title" style={{ width: '100%' }}>
					<Input.TextArea autoSize showCount required style={{ width: '100%' }} />
				</Form.Item>

				<label>{txt('cms.url')}</label>
				<Form.Item name="url">
					<Input required />
				</Form.Item>

				<label>{txt('cms.metaDescription')}</label>
				<Form.Item name="metaDescription" style={{ width: '100%' }}>
					<Input.TextArea autoSize showCount required style={{ width: '100%' }} />
				</Form.Item>

				<label>{txt('cms.keywords')}</label>
				<Form.Item name="keywords">
					<Select mode="tags" style={{ width: '100%' }} />
				</Form.Item>

				<label>{txt('cms.region')}</label>
				<Form.Item name="region">
					<Select style={{ width: '100%' }}>
						<Select.Option value="dk">Dk</Select.Option>
						<Select.Option value="se">Se</Select.Option>
						<Select.Option value="de">De</Select.Option>
					</Select>
				</Form.Item>

				<label>{txt('cms.published')}</label>
				<Form.Item name="published" valuePropName="checked">
					<Switch />
				</Form.Item>

				<Divider />
				<h3>{txt('cms.logged in redirect')}</h3>

				<label>{txt('cms.redirect')}</label>
				<Form.Item name={['content', 'loggedInRedirect']} initialValue="">
					<Input placeholder={txt('cms.redirect')} allowClear />
				</Form.Item>

				<Divider />
				<h3>{txt('cms.signup')}</h3>

				<label>{txt('cms.redirect')}</label>
				<Form.Item name={['content', 'signupRedirect']}>
					<Input placeholder={txt('cms.redirect')} allowClear />
				</Form.Item>

				<div className="row-container">
					<div>
						<label>{txt('cms.override')}</label>
						<Form.Item
							valuePropName="checked"
							name={['content', 'signupDialog', 'override']}
						>
							<Switch />
						</Form.Item>
					</div>

					<div>
						<label>{txt('cms.background')}</label>
						<Form.Item
							name={['content', 'signupDialog', 'backgroundColor']}
							initialValue="#ffffff"
							noStyle
						>
							<Input type="color" />
						</Form.Item>
					</div>
				</div>

				<div className="flex-vertical">
					<div>
						<label>
							<b>{txt('cms.text')}</b>
						</label>

						<div className="row-container">
							<div>
								<label>{txt('cms.text')}</label>
								<Form.Item
									name={['content', 'signupDialog', 'textColor']}
									initialValue="#000000"
									noStyle
								>
									<Input type="color" />
								</Form.Item>
							</div>

							<div>
								<label>{txt('cms.header')}</label>
								<Form.Item
									name={['content', 'signupDialog', 'headerTextColor']}
									initialValue="#2A6592"
									noStyle
								>
									<Input type="color" />
								</Form.Item>
							</div>

							<div>
								<label>{txt('cms.link')}</label>
								<Form.Item
									name={['content', 'signupDialog', 'linkTextColor']}
									initialValue="#2A6592"
									noStyle
								>
									<Input type="color" />
								</Form.Item>
							</div>
						</div>
					</div>

					<div>
						<label>
							<b>{txt('cms.input')}</b>
						</label>

						<div className="row-container">
							<div>
								<label>{txt('cms.text')}</label>
								<Form.Item
									name={['content', 'signupDialog', 'inputTextColor']}
									initialValue="#000000"
									noStyle
								>
									<Input type="color" />
								</Form.Item>
							</div>

							<div>
								<label>{txt('cms.background')}</label>
								<Form.Item
									name={['content', 'signupDialog', 'inputBackgroundColor']}
									initialValue="#FFFFFF"
									noStyle
								>
									<Input type="color" />
								</Form.Item>
							</div>

							<div>
								<label>{txt('cms.placeholder')}</label>
								<Form.Item
									name={['content', 'signupDialog', 'placeholderTextColor']}
									initialValue="#999999"
									noStyle
								>
									<Input type="color" />
								</Form.Item>
							</div>
						</div>
					</div>

					<div>
						<label>
							<b>{txt('cms.button')}</b>
						</label>

						<div className="row-container">
							<div>
								<label>{txt('cms.text')}</label>
								<Form.Item
									name={['content', 'signupDialog', 'buttonTextColor']}
									initialValue="#FFFFFF"
									noStyle
								>
									<Input type="color" />
								</Form.Item>
							</div>

							<div>
								<label>{txt('cms.background')}</label>
								<Form.Item
									name={['content', 'signupDialog', 'buttonBackgroundColor']}
									initialValue="#2A6592"
									noStyle
								>
									<Input type="color" />
								</Form.Item>
							</div>
						</div>
					</div>
				</div>

				<Divider />
				<h3>{txt('cms.topbar')}</h3>

				<div className="row-container">
					<div>
						<label>{txt('cms.override')}</label>
						<Form.Item
							name={['content', 'topbar', 'override']}
							valuePropName="checked"
							initialValue={false}
						>
							<Switch />
						</Form.Item>
					</div>

					<div className="flex-1-container">
						<label>{txt('cms.opacity')}</label>

						<div style={{ display: 'flex' }}>
							<Form.Item
								name={['content', 'topbar', 'backgroundOpacity']}
								initialValue={255}
								noStyle
							>
								<Slider min={0} max={255} style={{ flex: 1 }} />
							</Form.Item>

							<Form.Item
								name={['content', 'topbar', 'backgroundOpacity']}
								initialValue={255}
							>
								<InputNumber
									bordered={false}
									size="small"
									min={0}
									max={255}
									style={{ width: '6ch' }}
								/>
							</Form.Item>
						</div>
					</div>
				</div>

				<div className="row-container">
					<div>
						<label>{txt('cms.color')}</label>
						<Form.Item
							name={['content', 'topbar', 'backgroundColor']}
							initialValue="#edf7f9"
							noStyle
						>
							<Input type="color" />
						</Form.Item>
					</div>

					<div className="flex-1-container">
						<label>{txt('cms.blur')}</label>
						<div style={{ display: 'flex' }}>
							<Form.Item
								name={['content', 'topbar', 'backgroundBlur']}
								initialValue={0}
								noStyle
							>
								<Slider min={0} max={50} style={{ flex: 1 }} />
							</Form.Item>

							<Form.Item
								name={['content', 'topbar', 'backgroundBlur']}
								initialValue={0}
							>
								<InputNumber
									bordered={false}
									size="small"
									min={0}
									max={50}
									style={{ width: '6ch' }}
								/>
							</Form.Item>
						</div>
					</div>
				</div>

				<div className="row-container">
					<div className="flex-1-container">
						<label>{txt('cms.link color')}</label>
						<Form.Item name={['content', 'topbar', 'linkColor']} initialValue="#2a6592">
							<Input type="color" />
						</Form.Item>
					</div>

					<div className="flex-1-container">
						<label>{txt('cms.button background color')}</label>
						<Form.Item
							name={['content', 'topbar', 'buttonBackgroundColor']}
							initialValue="#2a6592"
						>
							<Input type="color" />
						</Form.Item>
					</div>

					<div className="flex-1-container">
						<label>{txt('cms.button text color')}</label>
						<Form.Item
							name={['content', 'topbar', 'buttonTextColor']}
							initialValue="#ffffff"
						>
							<Input type="color" />
						</Form.Item>
					</div>
				</div>

				<div className="flex-1-container">
					<label>{txt('cms.logo link')}</label>
					<Form.Item name={['content', 'logoLink']}>
						<Input allowClear />
					</Form.Item>
				</div>

				<div className="row-container">
					<div className="flex-1-container">
						<label>{txt('cms.logo')}</label>
						<Form.Item name={['content', 'logo']}>
							<Input allowClear />
						</Form.Item>
					</div>

					<div>
						<label>{txt('cms.hide links')}</label>
						<Form.Item
							name={['content', 'hideNavigationLinks']}
							valuePropName="checked"
						>
							<Switch />
						</Form.Item>
					</div>
				</div>

				<Divider />
				<h3>{txt('cms.bottombar')}</h3>

				<div className="row-container centered-content-container">
					<div>
						<label>{txt('cms.override')}</label>
						<Form.Item
							name={['content', 'bottombar', 'override']}
							valuePropName="checked"
						>
							<Switch />
						</Form.Item>
					</div>

					<div style={{ flex: 0.4 }}>
						<label>{txt('cms.background')}</label>
						<Form.Item
							name={['content', 'bottombar', 'backgroundColor']}
							initialValue="#DEF3FC"
						>
							<Input type="color" />
						</Form.Item>
					</div>
				</div>

				<div className="row-container">
					<div className="flex-1-container">
						<label>{txt('cms.title')}</label>
						<Form.Item
							name={['content', 'bottombar', 'titleColor']}
							initialValue="#000000"
						>
							<Input type="color" />
						</Form.Item>
					</div>

					<div className="flex-1-container">
						<label>{txt('cms.link color')}</label>
						<Form.Item
							name={['content', 'bottombar', 'linkColor']}
							initialValue="#2a6592"
						>
							<Input type="color" />
						</Form.Item>
					</div>

					<div className="flex-1-container">
						<label>{txt('cms.copyrights')}</label>
						<Form.Item
							name={['content', 'bottombar', 'copyrightsColor']}
							initialValue="#333333"
						>
							<Input type="color" />
						</Form.Item>
					</div>
				</div>

				<Divider />

				<div className="date-container">
					<div>
						<label>{txt('cms.created')}</label>
						<div>{formatDate(new Date(props.page.created))}</div>
					</div>

					<div>
						<label>{txt('cms.updated')}</label>
						<div>{formatDate(new Date(props.page.updated))}</div>
					</div>
				</div>
			</div>

			<div className="submit-container">
				<Button type="primary" htmlType="submit" loading={isUpdating}>
					{txt('cms.updatePage')}
				</Button>
			</div>
		</FormStyled>
	);
};

const FormStyled = styled(Form)`
	height: 100%;

	.inputs-container {
		padding: 2ch;
		overflow-y: scroll;
	}

	.row-container {
		display: flex;
		gap: 1rem;
	}

	.flex-1-container {
		flex: 1;
	}

	.centered-content-container {
		justify-content: center;
	}

	.flex-vertical {
		display: flex;
		flex-direction: column;
		gap: 1rem;
	}

	.date-container {
		display: flex;
		justify-content: space-between;
		align-items: center;

		> div {
			display: flex;
			flex-direction: column;
		}
	}

	.submit-container {
		border-top: 1px solid #eee;
		display: flex;
		gap: 1ch;
		padding-top: 1rem;
		justify-content: flex-end;
		margin-top: auto;
		padding: 2ch;
	}
`;
